import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getEnv } from "../../helpers/env";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  InputAdornment,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Code as CodeIcon,
  Storage as StorageIcon,
  CloudUpload as CloudUploadIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  ReportOutlined as ReportOutlinedIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";

import withErrorHandling from "../../HOC/ErrorHandling";
import { useSharedProps } from "../../HOC/SharedProps";
import t from "../../helpers/en";
import {
  getGitSecrets,
  setWizard3,
  getRegistrySecrets,
  getSecret,
  getConfigsAndSecrets,
  updateWizard3,
  getServiceCatalog,
  getServiceCatalogByPath,
  getClustersInstances,
  newServiceCatalog,
} from "../../helpers/utils";
import {
  GIT_NEW_CREDENTIALS,
  REGISTRY_NEW_CREDENTIALS,
  PRIVATE,
  PRIVATE_REGISTRY,
  JSON_ADD_VARIABLES,
} from "../../helpers/constants";
import {
  changeTxt,
  selectOption,
  configsAndSecretsShowPwd,
  setConfigsAndSecrets,
  changeSubTxt,
  setJsonVariable2Table,
  onValidateForm,
  onValidateFormStep,
  onValidateField,
} from "../../helpers/selectors";

import Select from "../Inputs/Select";
import SetConfigsAndSecrets from "../SetConfigsAndSecrets";
import Loader from "../Loading";
import StepIndicator from "./StepIndicator";
import WizardStep, { AnimatedField, SectionDivider } from "./WizardStep";
import GuidedDrawer from "./GuidedDrawer";
import TemplatePickerGrid from "./TemplatePickerGrid";
import TemplateProvisionDialog from "./TemplateProvisionDialog";
import { RocketLaunch as RocketLaunchIcon } from "@mui/icons-material";

// Constants
const PUBLIC = "PUBLIC";
const PLATFORM_REGISTRY = "PLATFORM_REGISTRY";
const ACCESS_TYPE = [
  { id: PUBLIC, name: t.public },
  { id: PRIVATE, name: t.private },
];
const REGISTRY_ACCESS_TYPE = [
  { id: PLATFORM_REGISTRY, name: t.platformHostedRegistry },
  { id: PRIVATE_REGISTRY, name: t.private },
];
const timezones = [
  { id: "PST", label: "PST" },
  { id: "CST", label: "CST" },
  { id: "EST", label: "EST" },
];
const MAIN_BRANCH = "main";
const CUSTOM_BRANCH = "custom";
const MASTER_BRANCH = "master";
const APPLICATION_SIZES = [
  { id: "TINY", label: "Tiny" },
  { id: "SMALL", label: "Small" },
  { id: "MEDIUM", label: "Medium" },
  { id: "LARGE", label: "Large" },
];
const GIT_BRANCHES = [
  { id: MAIN_BRANCH, label: "main" },
  { id: MASTER_BRANCH, label: "master" },
  { id: CUSTOM_BRANCH, label: "Other branch…" },
];

const INITIAL_STATE_FORM = {
  git_accessType: PUBLIC,
  registry_accessType: PLATFORM_REGISTRY,
  name: "system-default",
  deployAutomatically: true,
  scaleTo0: true,
  db_deploy: false,
  variables: [{ key: null, value: null }],
  secrets: [{ key: null, value: null, isPwdVisible: false }],
  configs_values: [],
  tabVal: 0,
  git_revision_selector: MAIN_BRANCH,
  git_revision: "main",
  instancesCatalog: [],
  cluster_name: "default",
  application_size: "SMALL",
};

// Step definitions with icons
const WIZARD_STEPS = [
  { id: "basics", label: "Basics", icon: CodeIcon },
  { id: "source", label: "Source", icon: StorageIcon },
  { id: "registry", label: "Image Storage", icon: CloudUploadIcon },
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "review", label: "Review", icon: CheckCircleIcon },
];

// Styled TextField with animations
const AnimatedTextField = ({ delay = 0, ...props }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <AnimatedField delay={delay}>
      <TextField
        {...props}
        fullWidth
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: isDarkMode ? alpha("#1e293b", 0.5) : "#ffffff",
            borderRadius: "10px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: isDarkMode ? alpha("#1e293b", 0.7) : "#f8fafc",
            },
            "&.Mui-focused": {
              backgroundColor: isDarkMode ? alpha("#1e293b", 0.8) : "#ffffff",
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
          },
          "& .MuiInputLabel-root": {
            color: isDarkMode ? "#94a3b8" : "#64748b",
          },
          ...props.sx,
        }}
      />
    </AnimatedField>
  );
};

const SOURCE_GIT = "git";
const SOURCE_TEMPLATE = "template";

const AddAppWizard = (props) => {
  const {
    showError,
    triggerUpdateTable,
    handleClose,
    data,
    onDetectFormChange,
    apps,
  } = props;

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();

  // Top-level source switcher: "From Git" (existing wizard, default)
  // vs "From Template" (Daari guided sample apps, app_templates table).
  // Only offered for brand-new apps — editing an existing app always
  // stays on the Git flow.
  const [sourceTab, setSourceTab] = useState(SOURCE_GIT);
  const [pickedTemplate, setPickedTemplate] = useState(null);

  const [form, setForm] = useState({ ...INITIAL_STATE_FORM });
  const [configs, setConfigs] = useState([]);
  const [gitCredentials, setGitCredentials] = useState([]);
  const [instancesCatalog, setInstancesCatalog] = useState([]);
  const [registryCredentials, setRegistryCredentials] = useState({});
  const [clusters, setClusters] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  // Guided-starter drawer state. Auto-opens once when the wizard
  // mounts for a NEW app; users editing an existing app skip it.
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [guidedAutoOpened, setGuidedAutoOpened] = useState(false);
  const [pickedSample, setPickedSample] = useState(null);
  // Service-catalog type metadata (settings.view field defs per path) — used
  // to auto-provision a sample's declared daariServices with sane defaults.
  const [catalogTypes, setCatalogTypes] = useState([]);
  // Per-service progress for the auto-provision-and-bind flow, keyed by
  // service type: "pending" | "provisioning" | "bound" | "failed" | "manual".
  const [daariProgress, setDaariProgress] = useState({});

  const { currentGroup } = useSharedProps();

  // Colors
  const colors = useMemo(
    () => ({
      cardBg: isDarkMode ? "#1e293b" : "#ffffff",
      cardBorder: isDarkMode ? "#334155" : "#e2e8f0",
      footerBg: isDarkMode ? "#0f172a" : "#f8fafc",
      buttonPrimary: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)",
      buttonSecondary: isDarkMode ? "#334155" : "#e2e8f0",
      textPrimary: isDarkMode ? "#f1f5f9" : "#1e293b",
      textSecondary: isDarkMode ? "#94a3b8" : "#64748b",
    }),
    [isDarkMode]
  );

  // Fetch functions
  const fetchGitCredential = useCallback(() => {
    return new Promise((resolve, reject) => {
      getGitSecrets((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = [
            ...result.map((it) => ({
              git_cred_username: it?.data?.username,
              git_cred_password: it?.data?.password,
              id: it?.metadata?.name,
              name: it?.metadata?.displayName,
              default: Boolean(it?.metadata?.labels?.default),
            })),
            { id: GIT_NEW_CREDENTIALS, name: t.newCredentials },
          ];
          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  }, []);

  const fetchRegistryCredential = useCallback(() => {
    return new Promise((resolve, reject) => {
      getRegistrySecrets((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = [
            ...result.map((it) => ({
              registry_cred_server: it?.data?.server,
              registry_cred_region: it?.metadata?.labels?.region,
              registry_cred_username: it?.data?.username,
              registry_cred_password: it?.data?.password,
              id: it?.metadata?.name,
              name: it?.metadata?.displayName,
              default: Boolean(it?.metadata?.labels?.default),
            })),
            { id: REGISTRY_NEW_CREDENTIALS, name: t.newRegistry },
          ];
          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  }, []);

  const fetchRegistrySecrets = useCallback(() => {
    return new Promise((resolve, reject) => {
      getConfigsAndSecrets((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = result.map((it) => ({
            displayName: it?.displayName,
            name: it?.name,
          }));
          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  }, []);

  const fetchInstances = useCallback(() => {
    return new Promise((resolve, reject) => {
      getServiceCatalogByPath(null, (result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = result.map((it) => ({
            serviceCatalogName: it.serviceCatalogName,
            resources: it.resources?.map((r) => ({
              name: r.metadata.labels?.["serviceCatalog.bindingName"],
              displayName:
                r.metadata.labels?.["serviceCatalog.bindingDisplayName"],
            })),
          }));
          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  }, []);

  // Service-catalog type list (with settings.view field defs) — needed to
  // build a valid create payload per daariServices[i].type without hardcoding
  // field names that the coordinator owns (e.g. "name").
  const fetchCatalogTypes = useCallback(() => {
    return new Promise((resolve, reject) => {
      getServiceCatalog(null, (result, err) => {
        if (Array.isArray(result)) {
          resolve(result);
        } else {
          reject(err);
        }
      });
    });
  }, []);

  const fetchClustersInstances = useCallback(() => {
    return new Promise((resolve, reject) => {
      getClustersInstances((result, err) => {
        if (Array.isArray(result)) {
          const updatedResult = [
            { id: "default", name: "Default" },
            ...result.map((it) => ({
              id: it.metadata.name,
              name: it.metadata.displayName,
            })),
          ];
          resolve(updatedResult);
        } else {
          reject(err);
        }
      });
    });
  }, []);

  const formatTime = (hour, min) => {
    return `${hour.toString().padStart(2, "0")}:${min
      .toString()
      .padStart(2, "0")}`;
  };

  // Load initial data
  useEffect(() => {
    setIsLoading(true);

    Promise.all([
      fetchGitCredential(),
      fetchRegistryCredential(),
      fetchRegistrySecrets(),
      fetchInstances(),
      fetchClustersInstances(),
      fetchCatalogTypes(),
    ])
      .then(
        ([
          gitResult,
          registryResult,
          configResult,
          instancesCatalog,
          clustersResult,
          catalogTypesResult,
        ]) => {
          if (Array.isArray(gitResult)) setGitCredentials(gitResult);
          if (Array.isArray(registryResult)) setRegistryCredentials(registryResult);
          if (Array.isArray(configResult)) setConfigs(configResult);
          if (Array.isArray(instancesCatalog)) setInstancesCatalog(instancesCatalog);
          if (Array.isArray(clustersResult)) setClusters(clustersResult);
          if (Array.isArray(catalogTypesResult)) setCatalogTypes(catalogTypesResult);

          if (data?.isEditForm || data?.isSetJson) {
            setForm({
              displayName: data?.displayName,
              git_repo: data.git?.repo,
              git_revision_selector:
                data.git?.revision === MAIN_BRANCH ||
                data.git?.revision === MASTER_BRANCH
                  ? data.git?.revision
                  : CUSTOM_BRANCH,
              git_revision: data.git?.revision,
              git_accessType: data.git?.accessType,
              git_secrets: data.git?.secrets?.name,
              git_cred_name: data.git?.secrets?.name,
              registry_cred_name: data.imagePullSecrets?.name,
              registry_accessType: data.imagePullSecrets?.registryType,
              registry_secrets: data.imagePullSecrets?.name,
              registry_cred_server: data.imagePullSecrets?.server,
              registry_cred_region: data.imagePullSecrets?.region,
              deployAutomatically: data.deployAutomatically,
              container_port: data.service?.containerPort,
              variables: Object.entries(data.service?.envParams || {})?.map(
                ([key, val]) => ({ key, value: val })
              ),
              secrets: Object.entries(data.service?.secretParams || {})?.map(
                ([key, val]) => ({ key, value: val })
              ),
              configs_values: configResult.filter((it) =>
                (data.service?.envFrom || []).some((el) => el === it.name)
              ),
              scaleTo0: data.service?.scaleTo0,
              db_deploy: false,
              tabVal: 0,
              up_time: formatTime(
                data.service?.dailyScaling?.up?.[0] || 0,
                data.service?.dailyScaling?.up?.[1] || 0
              ),
              down_time: formatTime(
                data.service?.dailyScaling?.down?.[0] || 0,
                data.service?.dailyScaling?.down?.[1] || 0
              ),
              timezone: data.service?.dailyScaling?.timezone,
              isEditForm: data.isEditForm,
              instancesCatalog: data?.service?.envVars?.serviceBinding?.bindings
                ?.map((it) =>
                  instancesCatalog
                    ?.flatMap((group) => group?.resources)
                    ?.find((cat) => cat?.name === it)
                )
                ?.filter((item) => item !== undefined),
              cluster_name: data.clusterName,
              application_size: data?.service?.applicationSize,
            });
          }
        }
      )
      .catch((err) => {
        showError(err);
      })
      .finally(() => setIsLoading(false));
  }, [
    data,
    showError,
    currentGroup,
    fetchGitCredential,
    fetchRegistryCredential,
    fetchRegistrySecrets,
    fetchInstances,
    fetchClustersInstances,
    fetchCatalogTypes,
  ]);

  // Auto-open the guided-starter drawer ONLY for true first-timers:
  // user has never seen the drawer on this browser AND this is a new
  // app (not an edit). Returning users get the banner as a button —
  // they can pull the drawer open when they want it. Tracked via a
  // localStorage flag so it survives session boundaries; users can
  // reset by clearing site data.
  const GUIDED_SEEN_KEY = "c2a:guided-drawer:seen:v1";
  useEffect(() => {
    if (isLoading) return;
    if (guidedAutoOpened) return;
    if (data?.isEditForm || form?.isEditForm) {
      setGuidedAutoOpened(true);
      return;
    }
    let alreadySeen = false;
    try {
      alreadySeen = window.localStorage.getItem(GUIDED_SEEN_KEY) === "1";
    } catch (_) {
      // localStorage blocked (private window etc) — fall back to
      // not auto-opening, so we don't bug returning users.
      alreadySeen = true;
    }
    if (!alreadySeen) {
      setGuidedOpen(true);
      try {
        window.localStorage.setItem(GUIDED_SEEN_KEY, "1");
      } catch (_) {
        // ignore
      }
    }
    setGuidedAutoOpened(true);
  }, [isLoading, guidedAutoOpened, data, form]);

  // When the user picks a sample, pre-fill the wizard with proven
  // defaults so they land on "deploy in 3 minutes". Fields not in the
  // sample stay as the wizard defaults.
  const applySample = useCallback((sample) => {
    setPickedSample(sample);
    setDaariProgress({});
    setForm((prev) => ({
      ...prev,
      // Default ON: sample templates that declare daariServices provision +
      // auto-bind them for the user unless they opt out on the Settings step.
      daariAutoProvision: (sample.daariServices?.length || 0) > 0,
      git_repo: sample.repoUrl,
      git_accessType: PUBLIC,
      git_revision_selector: sample.branch === "main" ? MAIN_BRANCH : CUSTOM_BRANCH,
      git_revision: sample.branch || "main",
      application_size: sample.applicationSize || prev.application_size,
      // container_port is the field the wizard reads for "what port
      // does this app listen on". Pre-filling from samples.json is
      // the difference between "happy path" and "user wonders why
      // the service routes nowhere".
      container_port: sample.port || prev.container_port,
      // Turn OFF scaleTo0 for samples — it's a scheduled-scaling
      // feature that requires up_time/down_time/timezone, which
      // become required fields the user has to puzzle out. Demos
      // don't need schedule-based scaling; ship always-on and let
      // the user enable scheduled scaling later if they want.
      scaleTo0: false,
      // displayName flows into the ECR repository name on submit,
      // which has a strict regex (lowercase, no spaces/+/dots).
      // Pre-fill the k8s-safe sample.appName ("my-react-app", not
      // "React + TypeScript") so Create works without the user
      // needing to know ECR naming rules. Preserve their input if
      // they already typed something.
      displayName: prev.displayName ? prev.displayName : (sample.appName || sample.displayName),
    }));
  }, []);

  // Form handlers
  const getRequiredFields = useCallback(() => {
    const gitFields = {};
    if (form?.git_accessType === PRIVATE) {
      gitFields.isGitSecretsRequired = true;
    }
    if (
      form?.git_secrets === GIT_NEW_CREDENTIALS &&
      form?.git_accessType === PRIVATE
    ) {
      gitFields.isGitCredUsernameRequired = true;
      gitFields.isGitCredPasswordRequired = true;
    }

    const registryFields = {};
    if (form?.registry_accessType === PRIVATE_REGISTRY) {
      registryFields.isRegistrySecretsRequired = true;
    }
    if (
      form?.registry_accessType === PRIVATE_REGISTRY &&
      form?.registry_secrets === REGISTRY_NEW_CREDENTIALS
    ) {
      registryFields.isRegistryCredUsernameRequired = true;
      registryFields.isRegistryCredPasswordRequired = true;
    }

    return { ...gitFields, ...registryFields };
  }, [form]);

  const onChangeTxt = useCallback(
    (evt) => {
      const result = changeTxt(evt, form);
      const formFields = getRequiredFields();

      setForm(result);
      onDetectFormChange?.();
      setErrors(
        onValidateField({ ...result, ...formFields }, errors, evt.target.name)
      );
    },
    [form, errors, getRequiredFields, onDetectFormChange]
  );

  const onSelectOption = useCallback(
    (_, value, param) => {
      let result = selectOption(_, value?.id ?? null, param, form);
      const formFields = getRequiredFields();

      if (param === "git_secrets") {
        result = {
          ...result,
          git_cred_username: value?.git_cred_username,
          git_cred_password: value?.git_cred_password,
          git_cred_name: value?.id,
        };
      } else if (param === "registry_secrets") {
        result = {
          ...result,
          registry_cred_username: value?.registry_cred_username,
          registry_cred_password: value?.registry_cred_password,
          registry_cred_server: value?.registry_cred_server,
          registry_cred_region: value?.registry_cred_region,
          registry_cred_name: value?.id,
        };
      } else if (param === "git_revision_selector") {
        result = {
          ...result,
          git_revision: value?.id === CUSTOM_BRANCH ? "" : value?.id,
        };
      }

      setForm(result);
      onDetectFormChange?.();
      setErrors(onValidateField({ ...result, ...formFields }, errors, param));
    },
    [form, errors, getRequiredFields, onDetectFormChange]
  );

  const onCheckboxChange = useCallback(
    (evt) => {
      const { name, checked } = evt.target;
      setForm((prev) => ({ ...prev, [name]: checked }));
      onDetectFormChange?.();
    },
    [onDetectFormChange]
  );

  const onSelectMultipleOptions = useCallback(
    (_event, values, param) => {
      setForm((prev) => ({ ...prev, [param]: values }));
      onDetectFormChange?.();
    },
    [onDetectFormChange]
  );

  const onToggleShowSecret = useCallback((key) => {
    setForm((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const onChangeTab = useCallback((_, val) => {
    setForm((prev) => ({ ...prev, tabVal: val }));
  }, []);

  const onShowPwd = useCallback(
    async (e, elem) => {
      const result = await configsAndSecretsShowPwd(e, {
        ...elem,
        showError,
        getSecret,
      });
      setForm(result);
    },
    [showError]
  );

  const onSetConfigsAndSecrets = useCallback(
    (evt, elem) => {
      const result = setConfigsAndSecrets(evt, elem);
      setForm(result);
      onDetectFormChange?.();
    },
    [onDetectFormChange]
  );

  const onChangeTxtJson = useCallback(
    (e) => {
      const value = e.target.value;
      setForm((prev) => ({
        ...prev,
        ...{ secondModal: { ...prev.secondModal, value } },
      }));
      onDetectFormChange?.();
    },
    [onDetectFormChange]
  );

  const onSetJsonVariable2Table = useCallback(() => {
    const result = setJsonVariable2Table(form);
    setForm(result);
    onDetectFormChange?.();
  }, [form, onDetectFormChange]);

  const onChangeSubTxt = useCallback(
    (evt, _val, valIdx, param) => {
      const result = changeSubTxt(evt, form, valIdx, param);
      setForm(result);
      onDetectFormChange?.();
    },
    [form, onDetectFormChange]
  );

  const onOpenSecond = useCallback(
    (evt, param) => {
      const { id } = evt.target;
      let cpy = { ...form };
      const envParams = form?.[param]?.reduce((acc, currentVal) => {
        return {
          ...acc,
          ...(currentVal.key ? { [currentVal.key]: currentVal.value } : {}),
        };
      }, {});

      cpy = {
        ...cpy,
        secondModal: {
          open: true,
          ...(id === JSON_ADD_VARIABLES
            ? { value: JSON.stringify(envParams, null, 2) }
            : {}),
        },
      };
      setForm(cpy);
    },
    [form]
  );

  const onHideModalSecond = useCallback(() => {
    setForm((prev) => ({ ...prev, secondModal: null }));
  }, []);

  const onChangeInstances = useCallback(
    (_evt, newValue) => {
      setForm((prev) => ({ ...prev, instancesCatalog: newValue }));
      onDetectFormChange?.();
    },
    [onDetectFormChange]
  );

  // Navigation
  const goToStep = useCallback(
    (step) => {
      if (step === currentStep) return;
      setDirection(step > currentStep ? 1 : -1);
      setCurrentStep(step);
    },
    [currentStep]
  );

  const goNext = useCallback(() => {
    const formFields = getRequiredFields();
    const validationErrors = onValidateFormStep(
      { ...form, ...formFields },
      currentStep
    );

    if (validationErrors && Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError(t.requiredMsg);
      return;
    }

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }

    if (currentStep < WIZARD_STEPS.length - 1) {
      goToStep(currentStep + 1);
    }
  }, [currentStep, form, getRequiredFields, completedSteps, goToStep, showError]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  // Provisions one Daari service (S3/DynamoDB/etc — any type present in
  // catalogTypes) with default field values from its settings.view, then
  // resolves to the created instance's { name, displayName } binding entry
  // (the same shape the "Attach managed services" Autocomplete uses) so it
  // can be folded into form.instancesCatalog before setWizard3/updateWizard3
  // is called. That's the existing serviceBinding wiring (K8sSecretsService.
  // serviceBindingCreateUpdate) — no separate bind call needed.
  const provisionDaariService = useCallback(
    (svc, appName) => {
      return new Promise((resolve) => {
        if (svc.manualOnly) {
          setDaariProgress((prev) => ({ ...prev, [svc.type]: "manual" }));
          resolve(null);
          return;
        }

        const catalogType = catalogTypes.find((it) => it.path === svc.type);
        if (!catalogType) {
          // No provisioning plumbing for this type yet (e.g. redis) —
          // surface as a gap rather than failing the whole app create.
          setDaariProgress((prev) => ({ ...prev, [svc.type]: "manual" }));
          resolve(null);
          return;
        }

        setDaariProgress((prev) => ({ ...prev, [svc.type]: "provisioning" }));

        const resourceName = `${appName}-${svc.type}`.slice(0, 63);
        const initialForm = {};
        catalogType?.settings?.view?.forEach((field) => {
          initialForm[field.fieldName] = field.defaultValue;
        });
        // "name" is the identifying field across postgres/awssqs/
        // awsdynamodb/awss3bucket — override the default with something
        // derived from the app so instances aren't all named "default".
        if (Object.prototype.hasOwnProperty.call(initialForm, "name") || catalogType?.settings?.view?.some((f) => f.fieldName === "name")) {
          initialForm.name = resourceName;
        }

        newServiceCatalog({ data: initialForm, path: svc.type }, (result, err) => {
          if (err) {
            setDaariProgress((prev) => ({ ...prev, [svc.type]: "failed" }));
            resolve(null);
            return;
          }

          setDaariProgress((prev) => ({ ...prev, [svc.type]: "bound" }));
          resolve({
            name: result?.metadata?.name || result?.name || resourceName,
            displayName:
              result?.metadata?.displayName || result?.displayName || resourceName,
          });
        });
      });
    },
    [catalogTypes]
  );

  // Save
  const onSave = useCallback(() => {
    const formFields = getRequiredFields();
    const validationErrors = onValidateForm({ ...form, ...formFields });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError(t.requiredMsg);
      return;
    }

    if (form?.isSubmitting) return;
    setForm((prev) => ({ ...prev, isSubmitting: true }));

    const triggerUpdate = (_result, err, cb) => {
      if (!err) {
        setErrors({});
        setForm({ ...INITIAL_STATE_FORM, isSubmitting: false });
        cb && cb();
      } else {
        setForm((prev) => ({ ...prev, isSubmitting: false }));
        showError(err);
      }
    };

    const submitApp = (finalForm) => {
      if (data?.isEditForm) {
        updateWizard3(
          { currentName: data?.metadata?.name, ...finalForm },
          (result, errorMsg) => {
            triggerUpdate(result, errorMsg, triggerUpdateTable);
          }
        );
      } else {
        setWizard3(finalForm, (result, errorMsg) => {
          triggerUpdate(result, errorMsg, triggerUpdateTable);
        });
      }
    };

    const shouldAutoProvision =
      form?.daariAutoProvision && pickedSample?.daariServices?.length > 0;

    if (!shouldAutoProvision) {
      submitApp(form);
      return;
    }

    const appName = form?.displayName || pickedSample?.appName || "app";
    Promise.all(
      pickedSample.daariServices.map((svc) => provisionDaariService(svc, appName))
    ).then((bindings) => {
      const newBindings = bindings.filter(Boolean);
      submitApp({
        ...form,
        instancesCatalog: [...(form?.instancesCatalog || []), ...newBindings],
      });
    });
  }, [
    form,
    data,
    getRequiredFields,
    showError,
    triggerUpdateTable,
    pickedSample,
    provisionDaariService,
  ]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <WizardStep
            stepKey="basics"
            direction={direction}
            title="Application Basics"
            subtitle="Give your application a name and select target cluster"
            icon={CodeIcon}
          >
            <AnimatedTextField
              delay={0}
              label={`${t.application} ${t.name}`}
              name="displayName"
              value={form?.displayName || ""}
              onChange={onChangeTxt}
              InputProps={{
                readOnly: form?.isEditForm,
                inputProps: {
                  // ECR repo names accept lowercase letters, digits,
                  // and . _ - (and / for sub-paths). Anything else
                  // 424s at Create. Constrain at the input so the
                  // user can't type something that will fail.
                  pattern: "^[a-z0-9]+([._-]+[a-z0-9]+)*$",
                  title: "Lowercase letters, digits, hyphens, dots only — no spaces.",
                  maxLength: 64,
                  autoCapitalize: "off",
                  spellCheck: false,
                },
              }}
              required
              error={!!errors.displayName || (!!form?.displayName && !/^[a-z0-9]+([._-]+[a-z0-9]+)*$/.test(form.displayName))}
              helperText={
                errors.displayName ||
                (form?.displayName && !/^[a-z0-9]+([._-]+[a-z0-9]+)*$/.test(form.displayName)
                  ? "Use lowercase letters, digits, hyphens, dots — no spaces or capitals."
                  : "Lowercase letters, digits, hyphens, dots only.")
              }
            />

            {getEnv("REACT_APP_MULTICLUSTER_ENABLED") && (
              <AnimatedField delay={0.1}>
                <Select
                  options={clusters}
                  onChange={(evt, val) => onSelectOption(evt, val, "cluster_name")}
                  label={t.targetKubernetesCluster}
                  item="name"
                  value={clusters?.find((it) => it.id === form?.cluster_name) || ""}
                  specificDetails={{
                    required: true,
                    disabled: form?.isEditForm,
                    getOptionDisabled: () => form?.isEditForm,
                  }}
                />
              </AnimatedField>
            )}
          </WizardStep>
        );

      case 1:
        return (
          <WizardStep
            stepKey="source"
            direction={direction}
            title="Source Code"
            subtitle="Configure your Git repository and branch"
            icon={StorageIcon}
          >
            <AnimatedTextField
              delay={0}
              label={`${t.git} ${t.repository}`}
              name="git_repo"
              value={form?.git_repo || ""}
              onChange={onChangeTxt}
              InputProps={{ readOnly: form?.isEditForm }}
              required
              error={!!errors.git_repo}
              helperText={errors.git_repo}
              placeholder="https://github.com/user/repo.git"
            />

            <AnimatedField delay={0.1}>
              <Select
                options={GIT_BRANCHES}
                onChange={(evt, val) => onSelectOption(evt, val, "git_revision_selector")}
                label={`${t.git} ${t.revision}`}
                item="label"
                value={GIT_BRANCHES?.find((it) => it.id === form?.git_revision_selector) || ""}
                specificDetails={{ errors, required: true, errorField: "git_revision_selector" }}
              />
            </AnimatedField>

            {form?.git_revision_selector === CUSTOM_BRANCH && (
              <AnimatedTextField
                delay={0.15}
                label="Branch name"
                name="git_revision"
                value={form?.git_revision || ""}
                onChange={onChangeTxt}
                required
                error={!!errors.git_revision}
              />
            )}

            <SectionDivider label="Access" />

            <AnimatedField delay={0.2}>
              <Select
                options={ACCESS_TYPE}
                onChange={(evt, val) => onSelectOption(evt, val, "git_accessType")}
                label={`${t.git} ${t.accessType}`}
                item="name"
                value={ACCESS_TYPE?.find((it) => it.id === form?.git_accessType) || ""}
              />
            </AnimatedField>

            {form?.git_accessType === PRIVATE && (
              <>
                <AnimatedField delay={0.25}>
                  <Select
                    options={gitCredentials}
                    onChange={(evt, val) => onSelectOption(evt, val, "git_secrets")}
                    label={t.privateRepository}
                    item="name"
                    value={gitCredentials?.find((it) => it.id === form?.git_secrets) || ""}
                    specificDetails={{
                      errors,
                      required: true,
                      errorField: "git_secrets",
                      styles: {
                        elemLi: (el) =>
                          (el?.default || el?.id === GIT_NEW_CREDENTIALS) && {
                            borderTop: `1px solid ${colors.cardBorder}`,
                          },
                      },
                    }}
                  />
                </AnimatedField>

                {form?.git_secrets === GIT_NEW_CREDENTIALS && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    sx={{
                      display: "grid",
                      gap: 2,
                      p: 2,
                      ml: 2,
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      borderRadius: "0 8px 8px 0",
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <AnimatedTextField
                      delay={0}
                      label={t.username}
                      name="git_cred_username"
                      value={form?.git_cred_username || ""}
                      onChange={onChangeTxt}
                      required
                      error={!!errors.git_cred_username}
                      helperText={errors.git_cred_username}
                    />
                    <AnimatedTextField
                      delay={0.05}
                      label="Secret / Token"
                      name="git_cred_password"
                      type={form?.showGitSecret ? "text" : "password"}
                      value={form?.git_cred_password || ""}
                      onChange={onChangeTxt}
                      required
                      error={!!errors.git_cred_password}
                      helperText={errors.git_cred_password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => onToggleShowSecret("showGitSecret")}
                              edge="end"
                              size="small"
                            >
                              {form?.showGitSecret ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </WizardStep>
        );

      case 2:
        return (
          <WizardStep
            stepKey="registry"
            direction={direction}
            title="Image Storage"
            subtitle="Where your built app images are stored before deployment"
            icon={CloudUploadIcon}
          >
            <AnimatedField delay={0}>
              <Select
                options={REGISTRY_ACCESS_TYPE}
                onChange={(evt, val) => onSelectOption(evt, val, "registry_accessType")}
                label={`${t.registry} ${t.accessType}`}
                item="name"
                value={REGISTRY_ACCESS_TYPE?.find((it) => it.id === form?.registry_accessType) || ""}
              />
            </AnimatedField>

            {form?.registry_accessType === PRIVATE_REGISTRY && (
              <>
                <AnimatedField delay={0.1}>
                  <Select
                    options={registryCredentials}
                    onChange={(evt, val) => onSelectOption(evt, val, "registry_secrets")}
                    label={t.registry}
                    item="name"
                    value={registryCredentials?.find((it) => it.id === form?.registry_secrets) || ""}
                    specificDetails={{
                      errors,
                      required: true,
                      errorField: "registry_secrets",
                      styles: {
                        elemLi: (el) =>
                          (el?.default || el?.id === REGISTRY_NEW_CREDENTIALS) && {
                            borderTop: `1px solid ${colors.cardBorder}`,
                          },
                      },
                    }}
                  />
                </AnimatedField>

                {form?.registry_secrets === REGISTRY_NEW_CREDENTIALS && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    sx={{
                      display: "grid",
                      gap: 2,
                      p: 2,
                      borderRadius: "12px",
                      border: `1px solid ${colors.cardBorder}`,
                      backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    }}
                  >
                    <AnimatedTextField
                      delay={0}
                      label={`${t.registry} ${t.name}`}
                      name="registry_name"
                      value={form?.registry_name || ""}
                      onChange={onChangeTxt}
                    />
                    <AnimatedTextField
                      delay={0.05}
                      label={t.server}
                      name="registry_cred_server"
                      value={form?.registry_cred_server || ""}
                      onChange={onChangeTxt}
                      placeholder="e.g., docker.io, gcr.io, 123456789.dkr.ecr.us-west-2.amazonaws.com"
                    />
                    {form?.registry_cred_server?.includes(".amazonaws.com") && (
                      <AnimatedTextField
                        delay={0.1}
                        label={t.region}
                        name="registry_cred_region"
                        value={form?.registry_cred_region || ""}
                        onChange={onChangeTxt}
                      />
                    )}
                    <AnimatedTextField
                      delay={0.15}
                      label={t.username}
                      name="registry_cred_username"
                      value={form?.registry_cred_username || ""}
                      onChange={onChangeTxt}
                      required
                      error={!!errors.registry_cred_username}
                      helperText={errors.registry_cred_username}
                    />
                    <AnimatedTextField
                      delay={0.2}
                      label="Secret"
                      name="registry_cred_password"
                      type={form?.showRegistrySecret ? "text" : "password"}
                      value={form?.registry_cred_password || ""}
                      onChange={onChangeTxt}
                      required
                      error={!!errors.registry_cred_password}
                      helperText={errors.registry_cred_password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => onToggleShowSecret("showRegistrySecret")}
                              edge="end"
                              size="small"
                            >
                              {form?.showRegistrySecret ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </WizardStep>
        );

      case 3:
        return (
          <WizardStep
            stepKey="settings"
            direction={direction}
            title="Application Settings"
            subtitle="Configure deployment, scaling, and environment"
            icon={SettingsIcon}
          >
            <AnimatedField delay={0}>
              <FormControlLabel
                label={t.deployAutomatically}
                control={
                  <Checkbox
                    checked={form.deployAutomatically}
                    onChange={onCheckboxChange}
                    name="deployAutomatically"
                  />
                }
              />
            </AnimatedField>

            {form.deployAutomatically && (
              <AnimatedField delay={0.05}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t.containerPort}
                    name="container_port"
                    value={form?.container_port || ""}
                    onChange={onChangeTxt}
                    required
                    error={!!errors?.container_port}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                      },
                    }}
                  />
                  <Tooltip
                    title="The port your application listens on (e.g., 3000 for React, 8080 for Java)"
                    arrow
                  >
                    <ReportOutlinedIcon sx={{ color: "#ff9800" }} />
                  </Tooltip>
                </Box>
              </AnimatedField>
            )}

            <SectionDivider label="Configuration" />

            <AnimatedField delay={0.1}>
              <Select
                multiple
                options={configs}
                onChange={(evt, val) => onSelectMultipleOptions(evt, val, "configs_values")}
                label={t.configsAndSecrets}
                item="displayName"
                value={form?.configs_values || []}
              />
            </AnimatedField>

            {pickedSample?.daariServices?.length > 0 && (
              <AnimatedField delay={0.12}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 1,
                    p: 2,
                    borderRadius: "12px",
                    border: `1px solid ${colors.cardBorder}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <FormControlLabel
                    label={`Also provision ${pickedSample.daariServices.length} Daari service${pickedSample.daariServices.length > 1 ? "s" : ""} for this app`}
                    control={
                      <Checkbox
                        checked={Boolean(form.daariAutoProvision)}
                        onChange={onCheckboxChange}
                        name="daariAutoProvision"
                      />
                    }
                  />
                  <Box sx={{ display: "grid", gap: 0.5, pl: 4 }}>
                    {pickedSample.daariServices.map((svc) => {
                      const progress = daariProgress[svc.type];
                      return (
                        <Box
                          key={svc.type}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: colors.textSecondary, fontSize: "0.82rem" }}
                          >
                            {svc.type}
                            {svc.manualOnly ? " (manual bind required — no auto-provision yet)" : ""}
                          </Typography>
                          {progress && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color:
                                  progress === "failed"
                                    ? theme.palette.error.main
                                    : progress === "bound"
                                    ? theme.palette.success.main
                                    : colors.textSecondary,
                              }}
                            >
                              {progress === "provisioning" && "Provisioning…"}
                              {progress === "bound" && "Bound"}
                              {progress === "failed" && "Failed"}
                              {progress === "manual" && "Skipped (manual bind)"}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </AnimatedField>
            )}

            <AnimatedField delay={0.15}>
              <Autocomplete
                multiple
                options={instancesCatalog.flatMap((group) => group.resources)}
                groupBy={(option) => {
                  const group = instancesCatalog.find((gr) =>
                    gr.resources.some((r) => r.name === option.name)
                  );
                  return group?.serviceCatalogName || "";
                }}
                value={form?.instancesCatalog || []}
                onChange={onChangeInstances}
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField {...params} label="Attach managed services (databases, queues…)" size="small" />
                )}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                getOptionLabel={(option) => option.displayName}
                size="small"
              />
            </AnimatedField>

            <SetConfigsAndSecrets
              form={form}
              onChangeTab={onChangeTab}
              onChangeSubTxt={onChangeSubTxt}
              onSetConfigsAndSecrets={onSetConfigsAndSecrets}
              onOpenSecond={onOpenSecond}
              onShowPwd={onShowPwd}
              onChangeTxtJson={onChangeTxtJson}
              onSetJsonVariable2Table={onSetJsonVariable2Table}
              onHideModalSecond={onHideModalSecond}
            />

            <SectionDivider label="Scaling" />

            <AnimatedField delay={0.2}>
              <FormControlLabel
                label={t.scaleTo0}
                control={
                  <Checkbox
                    checked={form.scaleTo0}
                    onChange={onCheckboxChange}
                    name="scaleTo0"
                  />
                }
              />
            </AnimatedField>

            {form.scaleTo0 && (
              <AnimatedField delay={0.25}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Wake up at"
                    type="time"
                    name="up_time"
                    value={form?.up_time || ""}
                    onChange={onChangeTxt}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                    error={!!errors.up_time}
                    helperText={errors.up_time}
                  />
                  <TextField
                    label="Sleep at"
                    type="time"
                    name="down_time"
                    value={form?.down_time || ""}
                    onChange={onChangeTxt}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                    error={!!errors.down_time}
                    helperText={errors.down_time}
                  />
                  <Select
                    options={timezones}
                    onChange={(evt, val) => onSelectOption(evt, val, "timezone")}
                    label="Timezone"
                    item="label"
                    value={timezones?.find((it) => it.id === form?.timezone) || ""}
                    specificDetails={{ errors, required: true, errorField: "timezone" }}
                  />
                </Box>
              </AnimatedField>
            )}

            <AnimatedField delay={0.3}>
              <Select
                options={APPLICATION_SIZES}
                onChange={(evt, val) => onSelectOption(evt, val, "application_size")}
                label={t.applicationSize}
                item="label"
                value={APPLICATION_SIZES?.find((it) => it.id === form?.application_size) || ""}
                specificDetails={{ required: true }}
              />
            </AnimatedField>
          </WizardStep>
        );

      case 4:
        return (
          <WizardStep
            stepKey="review"
            direction={direction}
            title={data?.isEditForm ? "Review & Update" : "Review & Create"}
            subtitle="Verify your configuration before proceeding"
            icon={CheckCircleIcon}
          >
            <Box sx={{ display: "grid", gap: 3 }}>
              {/* Basics Section */}
              <ReviewSection title="Basic Info">
                <ReviewItem label={`${t.application} ${t.name}`} value={form?.displayName} />
                {getEnv("REACT_APP_MULTICLUSTER_ENABLED") && (
                  <ReviewItem label={t.targetKubernetesCluster} value={form?.cluster_name} />
                )}
              </ReviewSection>

              {/* Source Section */}
              <ReviewSection title="Source Code">
                <ReviewItem label={`${t.git} ${t.repository}`} value={form?.git_repo} />
                <ReviewItem label={`${t.git} ${t.revision}`} value={form?.git_revision} />
                <ReviewItem label={`${t.git} ${t.accessType}`} value={form?.git_accessType} />
                {form?.git_accessType === PRIVATE && (
                  <ReviewItem label={t.privateRepository} value={form?.git_secrets} />
                )}
              </ReviewSection>

              {/* Registry Section */}
              <ReviewSection title="Image Storage">
                <ReviewItem label={`${t.registry} ${t.accessType}`} value={form?.registry_accessType} />
                {form?.registry_accessType === PRIVATE_REGISTRY && (
                  <>
                    <ReviewItem label={t.registry} value={form?.registry_secrets} />
                    {form?.registry_secrets === REGISTRY_NEW_CREDENTIALS && (
                      <ReviewItem label={t.server} value={form?.registry_cred_server} />
                    )}
                  </>
                )}
              </ReviewSection>

              {/* Settings Section */}
              <ReviewSection title="Application Settings">
                <ReviewItem
                  label={t.deployAutomatically}
                  value={form.deployAutomatically ? "Yes" : "No"}
                />
                {form.deployAutomatically && (
                  <ReviewItem label={t.containerPort} value={form?.container_port} />
                )}
                <ReviewItem label={t.scaleTo0} value={form.scaleTo0 ? "Yes" : "No"} />
                <ReviewItem label={t.applicationSize} value={form?.application_size} />
              </ReviewSection>
            </Box>
          </WizardStep>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
        }}
      >
        <Loader />
      </Box>
    );
  }

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "500px",
        backgroundColor: colors.cardBg,
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      {/* Top-level source switcher — only for brand-new apps. Editing
          an existing app always stays on the Git flow (templates are
          a create-time-only shortcut). */}
      {!form?.isEditForm && (
        <Box sx={{ px: 3, pt: 2, borderBottom: `1px solid ${colors.cardBorder}`, flexShrink: 0 }}>
          <Tabs
            value={sourceTab}
            onChange={(_e, val) => setSourceTab(val)}
            sx={{ minHeight: "40px", "& .MuiTab-root": { minHeight: "40px", textTransform: "none", fontWeight: 600 } }}
          >
            <Tab label="From Git" value={SOURCE_GIT} />
            <Tab label="From Template" value={SOURCE_TEMPLATE} />
          </Tabs>
        </Box>
      )}

      {sourceTab === SOURCE_TEMPLATE && !form?.isEditForm ? (
        <>
          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Guided sample apps come with their own Postgres/S3/DynamoDB/LLM
              Gateway services — pick one, name your app, and we provision
              everything for you.
            </Typography>
            <TemplatePickerGrid onSelect={(tpl) => setPickedTemplate(tpl)} />
          </Box>

          <TemplateProvisionDialog
            open={!!pickedTemplate}
            template={pickedTemplate}
            projectId={currentGroup?.id || currentGroup}
            existingAppNames={(apps || []).map((a) => a?.name || a?.metadata?.name).filter(Boolean)}
            showError={showError}
            onClose={() => setPickedTemplate(null)}
            onCreated={(result) => {
              setPickedTemplate(null);
              handleClose?.();
              triggerUpdateTable?.();
              if (result?.appId) {
                navigate(`/apps/${result.appId}/deploying`, {
                  state: { appName: result.appName, template: result.template },
                });
              }
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              borderTop: `1px solid ${colors.cardBorder}`,
              backgroundColor: colors.footerBg,
              flexShrink: 0,
            }}
          >
            <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: "10px", textTransform: "none", px: 3 }}>
              {t.cancel}
            </Button>
          </Box>
        </>
      ) : (
        <>
      <GuidedDrawer
        open={guidedOpen}
        onClose={() => setGuidedOpen(false)}
        onSelect={applySample}
      />

      {/* Starter banner — shows when no sample has been picked yet.
          Collapses to a compact pill once a sample is applied so the
          user can still re-open the catalog if they change their mind. */}
      {!form?.isEditForm && (
        <Box
          onClick={() => setGuidedOpen(true)}
          sx={{
            mx: 3,
            mt: 3,
            px: 2,
            py: pickedSample ? 1 : 1.5,
            borderRadius: 2,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: pickedSample
              ? "linear-gradient(135deg, rgba(234,88,12,0.14) 0%, rgba(15,23,42,0.4) 100%)"
              : "linear-gradient(135deg, rgba(234,88,12,0.10) 0%, rgba(99,102,241,0.10) 100%)",
            border: `1px solid ${pickedSample ? "rgba(234,88,12,0.35)" : "rgba(234,88,12,0.18)"}`,
            transition: "background 200ms ease, border-color 200ms ease, transform 150ms ease",
            "&:hover": {
              transform: "translateY(-1px)",
              borderColor: "rgba(234,88,12,0.55)",
            },
          }}
        >
          <RocketLaunchIcon sx={{ color: "#fb923c", fontSize: 22, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {pickedSample ? (
              <Typography variant="body2" sx={{ fontWeight: 500, color: isDarkMode ? "#fed7aa" : "#9a3412" }}>
                Using <strong>{pickedSample.displayName}</strong> starter — wizard pre-filled. Tap to change.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600, color: isDarkMode ? "#fed7aa" : "#9a3412" }}>
                  First time? Start with a sample app.
                </Typography>
                <Typography variant="caption" sx={{ color: isDarkMode ? "rgba(255,255,255,0.6)" : "#7c2d12" }}>
                  Pick React, Java, .NET, or Python — we pre-fill the wizard with a proven repo so your first deploy goes green.
                </Typography>
              </>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#ea580c",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {pickedSample ? "Change →" : "Browse starters →"}
          </Typography>
        </Box>
      )}

      {/* Step Indicator */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${colors.cardBorder}`,
          flexShrink: 0,
        }}
      >
        <StepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepClick={goToStep}
          completedSteps={completedSteps}
        />
      </Box>

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 3,
        }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          {renderStepContent()}
        </AnimatePresence>
      </Box>

      {/* Footer Actions */}
      <Box
        component={motion.div}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderTop: `1px solid ${colors.cardBorder}`,
          backgroundColor: colors.footerBg,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            px: 3,
          }}
        >
          {t.cancel}
        </Button>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          {currentStep > 0 && (
            <Button
              variant="outlined"
              onClick={goBack}
              startIcon={<KeyboardArrowLeft />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 2,
              }}
            >
              {t.previous}
            </Button>
          )}

          {!isLastStep ? (
            <Button
              variant="contained"
              onClick={goNext}
              endIcon={<KeyboardArrowRight />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 3,
                background: colors.buttonPrimary,
                "&:hover": {
                  background: colors.buttonPrimary,
                  filter: "brightness(1.1)",
                },
              }}
            >
              {t.next}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={onSave}
              disabled={form?.isSubmitting}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 4,
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  filter: "brightness(1.1)",
                },
              }}
            >
              {form?.isSubmitting ? (
                <Loader specificDetails={{ size: 20, style: { color: "#fff" } }} />
              ) : data?.isEditForm ? (
                t.update
              ) : (
                t.create
              )}
            </Button>
          )}
        </Box>
      </Box>
        </>
      )}
    </Box>
  );
};

// Review Section Component
const ReviewSection = ({ title, children }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: "12px",
          backgroundColor: isDarkMode ? alpha("#334155", 0.3) : "#f8fafc",
          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: isDarkMode ? "#94a3b8" : "#64748b",
            textTransform: "uppercase",
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.05em",
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: "grid", gap: 1 }}>{children}</Box>
      </Box>
    </motion.div>
  );
};

// Review Item Component
const ReviewItem = ({ label, value }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  if (!value) return null;

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography
        variant="body2"
        sx={{ color: isDarkMode ? "#94a3b8" : "#64748b", fontSize: "0.85rem" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: isDarkMode ? "#f1f5f9" : "#1e293b",
          fontWeight: 500,
          fontSize: "0.85rem",
          maxWidth: "60%",
          textAlign: "right",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default withErrorHandling(AddAppWizard);
